import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Popover, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { useCompanies } from '../../tasks/hooks/useTaskLookups.js';
import { useUpdateContactMutation } from '../hooks/useContactMutations.js';
import type { ContactItem } from '../api/contacts.client.js';

export function ContactCompanyField({ contact }: { contact: ContactItem }): ReactElement {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const companies = useCompanies();
  const update = useUpdateContactMutation();
  const [open, setOpen] = useState(false);
  const privateLabel = t('crm.features.features.contacts.pages.contactspage.частное_лицо');
  return (
    <div className="entity-company-field">
      {contact.companyRef ? (
        <Link to={`/companies/${contact.companyRef.id}`}>{contact.companyRef.name}</Link>
      ) : (
        contact.company || privateLabel
      )}
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        title={t('crm.entity_detail.company_edit')}
        content={
          <Select
            data-testid="contact-company-select"
            aria-label={t('crm.entity_detail.company_edit')}
            showSearch
            optionFilterProp="label"
            allowClear
            style={{ width: 240 }}
            value={contact.companyId ?? ''}
            loading={companies.isLoading || update.isPending}
            disabled={update.isPending || companies.isError}
            placeholder={privateLabel}
            options={[
              { value: '', label: privateLabel },
              ...(companies.data ?? []).map((company) => ({
                value: company.id,
                label: company.name,
              })),
            ]}
            onChange={(companyId: string | undefined) =>
              update.mutate(
                { id: contact.id, input: { companyId: companyId || null } },
                {
                  onSuccess: () => setOpen(false),
                  onError: () => {
                    void message.error(t('crm.entity_detail.save_error'));
                  },
                },
              )
            }
          />
        }
      >
        <Button
          data-testid="contact-company-edit"
          className="entity-company-edit"
          type="text"
          size="small"
          icon={<EditOutlined />}
          aria-label={t('crm.entity_detail.company_edit')}
        />
      </Popover>
      {companies.isError && open ? (
        <span role="alert" className="entity-meta">
          {t('crm.entity_detail.load_error')}
        </span>
      ) : null}
    </div>
  );
}
